import { createSelector } from "@reduxjs/toolkit";
import { PackageFareDetails, PackagePricing } from "redmond";
import { IStoreState } from "../../../reducers/types";
import { selectedFareDetailsSelector } from "../../flight-shop/reducer";
import { getSelectedPackageByLodgingId } from "../../hotel-shop/reducer";
import {
  getPackageDisplayData,
  getPackagedAirComponent,
  getPackagedRoomComponent,
} from "../../package-identity/reducer/selectors";
import {
  toPackagePricingFromSnapshot,
  toReviewPricing,
} from "../../hotel-shop/utils/packageSnapshotAdapters";

/**
 * Resolves package pricing from whichever source is populated — v0's selected fare details, or
 * v2's `GetPackage` snapshot. Mirrors {@link getPackageHotelIdentity}'s presence-based resolution
 * (the v2 snapshot is only ever populated under the v2 flag).
 *
 * v0's fallback to `selectedPackageByLodging.packageDetails.pricing` is a Review-only quirk
 * (`itinerary-review/component.tsx`'s original inline logic) — Book's own v0 resolution never had
 * this fallback, so it's preserved here as a parameter rather than baked into v0's default path.
 *
 * Memoized via `createSelector` — without it, every call rebuilds the v2-derived pricing
 * (`toPackagePricingFromSnapshot`/`toReviewPricing`) into a fresh object, and since
 * `mapStateToProps` re-runs on every Redux dispatch app-wide, that new reference would retrigger
 * any `useEffect` depending on it (e.g. Book's `INITIALIZE_CHECKOUT_STATE` effect, or
 * `PackagesBookTracking`'s view-tracking effect) on unrelated state changes. Two variants are
 * built — with and without the lodging fallback — since `createSelector` can't key its cache on an
 * extra, non-selector `options` argument.
 *
 * Both `createSelector` calls are deferred to first invocation (rather than built at module scope)
 * — some import orders reach this module mid-cycle, where a dependency's own selector exports
 * haven't been assigned yet; building the selectors eagerly would capture those `undefined`s (same
 * issue {@link getPackageHotelIdentity} guards against).
 */
const resolvePackagePricing = (
  fareDetails: PackageFareDetails | undefined,
  lodgingFallbackPricing: PackagePricing | undefined,
  packageDisplayData: ReturnType<typeof getPackageDisplayData>,
  packagedAirComponent: ReturnType<typeof getPackagedAirComponent>,
  packagedRoomComponent: ReturnType<typeof getPackagedRoomComponent>
): PackagePricing | undefined => {
  const v0Pricing = fareDetails?.packagePricing ?? lodgingFallbackPricing;
  if (v0Pricing != null) return v0Pricing;

  if (packageDisplayData != null) {
    return toPackagePricingFromSnapshot(
      packageDisplayData,
      packagedAirComponent,
      packagedRoomComponent
    );
  }

  return toReviewPricing(packagedRoomComponent);
};

const castFareDetails = (state: IStoreState) =>
  selectedFareDetailsSelector(state) as PackageFareDetails | undefined;

let memoizedWithoutLodgingFallback:
  | ((state: IStoreState) => PackagePricing | undefined)
  | undefined;

const getPackagePricingIdentityWithoutLodgingFallback = (
  state: IStoreState
): PackagePricing | undefined => {
  if (memoizedWithoutLodgingFallback == null) {
    memoizedWithoutLodgingFallback = createSelector(
      [
        castFareDetails,
        getPackageDisplayData,
        getPackagedAirComponent,
        getPackagedRoomComponent,
      ],
      (
        fareDetails,
        packageDisplayData,
        packagedAirComponent,
        packagedRoomComponent
      ) =>
        resolvePackagePricing(
          fareDetails,
          undefined,
          packageDisplayData,
          packagedAirComponent,
          packagedRoomComponent
        )
    );
  }

  return memoizedWithoutLodgingFallback(state);
};

let memoizedWithLodgingFallback:
  | ((state: IStoreState) => PackagePricing | undefined)
  | undefined;

const getPackagePricingIdentityWithLodgingFallback = (
  state: IStoreState
): PackagePricing | undefined => {
  if (memoizedWithLodgingFallback == null) {
    memoizedWithLodgingFallback = createSelector(
      [
        castFareDetails,
        getSelectedPackageByLodgingId,
        getPackageDisplayData,
        getPackagedAirComponent,
        getPackagedRoomComponent,
      ],
      (
        fareDetails,
        selectedPackageByLodging,
        packageDisplayData,
        packagedAirComponent,
        packagedRoomComponent
      ) =>
        resolvePackagePricing(
          fareDetails,
          selectedPackageByLodging?.packageDetails.pricing,
          packageDisplayData,
          packagedAirComponent,
          packagedRoomComponent
        )
    );
  }

  return memoizedWithLodgingFallback(state);
};

export const getPackagePricingIdentity = (
  state: IStoreState,
  options: { useLodgingFallback?: boolean } = {}
): PackagePricing | undefined =>
  options.useLodgingFallback
    ? getPackagePricingIdentityWithLodgingFallback(state)
    : getPackagePricingIdentityWithoutLodgingFallback(state);
